import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Check, CheckCircle, ArrowRight, Settings, CheckSquare,
  Shield, Lock, KeyRound, Monitor, Mail,
  AlertTriangle, FileText, Bell, XOctagon,
  Building2, FileBox, Users, Puzzle
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import { useGetModulesQuery } from '../redux/Clinictrainingapi';

const moduleCompletionData = {
  1: {
    items: [
      { text: 'What HIPAA is' },
      { text: 'What PHI means' },
      { text: 'Why compliance matters' },
      { text: 'Your responsibility' },
    ],
    subtitle: 'You now understand:',
    buttonText: 'Next',
    useCheckIcon: true,
    showProgressBar: false,
  },
  2: {
    items: [
      { text: 'When PHI can be shared' },
      { text: 'When written authorization is required' },
      { text: 'The Minimum Necessary rule' },
      { text: 'Patient rights' },
      { text: 'Common privacy mistakes' },
    ],
    subtitle: 'You now understand:',
    buttonText: 'Next',
    useCheckIcon: true,
    showProgressBar: true,
  },
  3: {
    items: [
      { text: 'How to protect electronic PHI', icon: Lock },
      { text: 'Password and access rules', icon: KeyRound },
      { text: 'Device security', icon: Monitor },
      { text: 'Email safety', icon: Mail },
      { text: 'Your role in preventing digital breaches', icon: Shield },
    ],
    subtitle: 'You now understand:',
    useModuleIcons: true,
    showProgressBar: true,
    useCardFooter: true,
  },
  4: {
    items: [
      { text: 'What a breach is', icon: AlertTriangle },
      { text: 'Common breach examples', icon: FileText },
      { text: 'Importance of immediate reporting', icon: Bell },
      { text: 'What not to do after a mistake', icon: XOctagon },
    ],
    subtitle: 'You now understand the critical aspects of breach management.',
    takeawaysLabel: 'MODULE TAKEAWAYS',
    showDecorative: true,
    showProgressBar: false,
  },
  5: {
    items: [
      { text: 'What a Business Associate is', icon: Building2 },
      { text: 'Why a BAA is required', icon: FileBox },
      { text: 'How responsibility is shared', icon: Users },
      { text: "Your clinic's role in protecting patient information", icon: Puzzle },
    ],
    subtitle: 'You now understand:',
    buttonText: 'Next Module',
    useModuleIcons: true,
    showProgressBar: false,
    showFinalAssessmentLabel: true,
  },
  6: {
    items: [
      { text: 'Daily compliance habits' },
      { text: 'Front desk and clinical safety' },
      { text: 'Admin and billing best practices' },
      { text: 'The 5-Second Compliance Rule' },
    ],
    subtitle: null,
    uppercaseTitle: true,
    useCheckIcon: true,
    useLearningCard: true,
    showModuleStepper: true,
    isFinalModule: true,
  },
};

export default function ModuleComplete() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { data: modules, isLoading } = useGetModulesQuery();

  // Always scroll to top on mount / module change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [moduleId]);

  if (isLoading) return <div className="p-8">Loading...</div>;

  const module = modules?.find(m => m.order === Number(moduleId));
  if (!module) return <div className="p-8">Module not found</div>;

  const sortedModules = [...(modules ?? [])].sort((a, b) => a.order - b.order);
  const currentIndex = sortedModules.findIndex(m => m.order === module.order);
  const nextModule = sortedModules[currentIndex + 1];
  const totalModules = sortedModules.length;

  const config = moduleCompletionData[module.order] || moduleCompletionData[1];
  const items = config.items;

  const handleNext = () => {
    if (nextModule) {
      navigate(`/module/${nextModule.order}`);
    } else {
      navigate('/dashboard');
    }
  };

  let buttonText = config.buttonText || 'Next';
  if (config.useCardFooter && nextModule) buttonText = `Continue to Module ${nextModule.order}`;
  if (!config.buttonText && !config.useCardFooter && nextModule) buttonText = `Continue To Module ${nextModule.order}`;
  if (!nextModule) buttonText = 'Back to Course';
  if (module.order === 1 || module.order === 2) buttonText = 'Next';
  if (module.order === 5) buttonText = 'Next Module';

  return (
    <div className="min-h-screen bg-surface">
      <Sidebar />

      {/* ml-0 on mobile, ml-16 on md+ where sidebar is visible */}
      <div className="ml-0 md:ml-16">
        <TopNav
          backTo="/dashboard"
          backLabel="COURSE OVERVIEW"
          breadcrumbs={[{ label: `MODULE ${module.order}`, to: `/module/${module.order}` }, 'COMPLETED']}
          rightContent={
            <span className="hidden sm:flex items-center gap-1.5 text-xs text-primary font-medium">
              CERTIFICATION TRACK
              <Settings size={14} />
            </span>
          }
        />

        {/* Outer padding — tighter on mobile */}
        <div className="flex justify-center px-4 sm:px-6 py-6 sm:py-12">
          <motion.div
            className="w-full max-w-2xl bg-white rounded-2xl border border-gray-200 p-5 sm:p-10 text-center relative overflow-hidden"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* Decorative background (Module 4) */}
            {config.showDecorative && (
              <>
                <div className="absolute top-6 right-8 text-gray-100 opacity-40">
                  <Settings size={64} strokeWidth={1} />
                </div>
                <div className="absolute bottom-12 left-8 text-gray-100 opacity-30">
                  <Check size={48} strokeWidth={1} />
                </div>
              </>
            )}

            {/* Animated checkmark — slightly smaller on mobile */}
            <motion.div
              className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-5 relative"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.2 }}
            >
              <div className="absolute inset-0 rounded-full border-[3px] border-gray-100" />
              <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 80 80">
                <motion.circle
                  cx="40" cy="40" r="37"
                  fill="none"
                  stroke="#0891b2"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray={232}
                  initial={{ strokeDashoffset: 232 }}
                  animate={{ strokeDashoffset: 58 }}
                  transition={{ duration: 1, delay: 0.4, ease: 'easeOut' }}
                />
              </svg>
              <div className="absolute inset-2.5 rounded-full bg-primary flex items-center justify-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.8, type: 'spring' }}
                >
                  <Check size={20} className="text-white sm:hidden" strokeWidth={3} />
                  <Check size={24} className="text-white hidden sm:block" strokeWidth={3} />
                </motion.div>
              </div>
            </motion.div>

            {/* Milestone label */}
            <motion.div
              className="text-xs font-bold text-primary tracking-[0.2em] uppercase mb-3"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              Milestone Reached
            </motion.div>

            {/* Title */}
            <motion.h1
              className={`font-bold text-gray-900 mb-2 ${
                config.uppercaseTitle || module.order === 3
                  ? 'text-xl sm:text-2xl uppercase tracking-wide'
                  : 'text-xl sm:text-2xl'
              }`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              You have completed Module {module.order}{module.order >= 2 ? '.' : ''}
            </motion.h1>

            {/* Subtitle */}
            {config.subtitle && (
              <motion.p
                className="text-gray-400 text-sm mb-6"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.75 }}
              >
                {config.subtitle}
              </motion.p>
            )}

            {/* Takeaways label (Module 4) */}
            {config.takeawaysLabel && (
              <motion.div
                className="text-xs font-bold text-primary tracking-[0.15em] uppercase mb-4"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                {config.takeawaysLabel}
              </motion.div>
            )}

            {/* Learning Summary Card (Module 6) */}
            {config.useLearningCard ? (
              <motion.div
                className="bg-gray-50/80 rounded-xl border border-gray-100 px-4 sm:px-6 py-5 sm:py-6 mb-8 text-left max-w-lg mx-auto"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85 }}
              >
                <div className="text-xs font-bold text-primary tracking-[0.15em] uppercase mb-3">
                  Learning Summary
                </div>
                <p className="text-sm font-semibold text-gray-900 mb-4">You now understand:</p>
                {/* Single col on mobile, 2 cols on sm+ */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {items.map((item, i) => (
                    <motion.div
                      key={i}
                      className="flex items-start gap-2.5"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.95 + i * 0.08 }}
                    >
                      <CheckCircle size={18} className="text-primary shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{item.text}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ) : (
              /* Standard completion items */
              <motion.div
                className="mb-8"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85 }}
              >
                {/* Single col on mobile, 2 cols on sm+ */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg mx-auto">
                  {items.map((item, i) => {
                    const isLastOdd = i === items.length - 1 && items.length % 2 !== 0;
                    const IconComponent = item.icon;

                    return (
                      <motion.div
                        key={i}
                        className={`flex items-center gap-3 bg-gray-50/80 rounded-xl px-4 py-3.5 border border-gray-100 ${
                          isLastOdd ? 'sm:col-span-2 sm:max-w-xs sm:mx-auto w-full' : ''
                        }`}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9 + i * 0.08 }}
                      >
                        {config.useModuleIcons && IconComponent ? (
                          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                            <IconComponent size={16} className="text-primary" />
                          </div>
                        ) : (
                          <CheckCircle size={18} className="text-primary shrink-0" />
                        )}
                        <span className="text-sm text-gray-700 text-left">{item.text}</span>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Footer section */}
            <motion.div
              className="pt-5 sm:pt-6 border-t border-gray-100 relative z-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3 }}
            >
              {config.isFinalModule ? (
                <>
                  {/* Module stepper — wrap on very small screens */}
                  {config.showModuleStepper && (
                    <div className="flex items-center justify-center flex-wrap gap-y-2 mb-6 sm:mb-8">
                      {sortedModules.map((m, i) => (
                        <div key={i} className="flex items-center">
                          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary flex items-center justify-center text-white text-xs font-bold">
                            {m.order}
                          </div>
                          {i < sortedModules.length - 1 && (
                            <div className="w-5 sm:w-8 h-0.5 bg-primary" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={() => navigate('/final-exam')}
                    className="w-full max-w-sm mx-auto flex items-center justify-center gap-2.5 bg-gradient-to-r from-primary to-cyan-400 hover:from-primary-700 hover:to-primary text-white px-6 sm:px-10 py-3.5 sm:py-4 rounded-full font-semibold text-sm sm:text-base transition-all cursor-pointer shadow-lg shadow-primary/25"
                  >
                    Start Final Exam
                    <CheckSquare size={18} />
                  </button>

                  <p className="text-xs sm:text-sm text-gray-400 mt-4">
                    Ready to earn your HIPAA Certification? The final exam covers all {totalModules} modules.
                  </p>
                </>
              ) : config.useCardFooter ? (
                <div className="bg-gray-50/60 rounded-xl border border-gray-100 px-4 sm:px-6 py-5 sm:py-6">
                  {nextModule && (
                    <p className="text-sm text-gray-400 mb-4">
                      Click Next to continue to{' '}
                      <span className="font-semibold text-gray-600">
                        Module {nextModule.order}: {nextModule.title}
                      </span>.
                    </p>
                  )}
                  <button
                    onClick={handleNext}
                    className="inline-flex items-center gap-2.5 bg-gradient-to-r from-primary to-cyan-400 hover:from-primary-700 hover:to-primary text-white px-8 sm:px-10 py-3.5 rounded-full font-semibold text-sm transition-all cursor-pointer shadow-lg shadow-primary/25"
                  >
                    {buttonText}
                    <ArrowRight size={16} />
                  </button>

                  {config.showProgressBar && (
                    <div className="flex items-center justify-center gap-2 mt-5">
                      <div className="flex gap-1">
                        {sortedModules.map((_, i) => (
                          <div
                            key={i}
                            className={`h-2 rounded-full transition-all ${
                              i < module.order
                                ? i === module.order - 1
                                  ? 'w-5 bg-primary'
                                  : 'w-2 bg-primary/50'
                                : 'w-2 bg-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-xs text-gray-400 font-medium tracking-wider">
                        PROGRESS {module.order}/{totalModules}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {nextModule && (
                    <p className="text-sm text-gray-400 mb-4">
                      Click Next to continue to Module {nextModule.order}: {nextModule.title}.
                    </p>
                  )}
                  {!nextModule && (
                    <p className="text-sm text-gray-400 mb-4">
                      Congratulations! You have completed all modules.
                    </p>
                  )}

                  <button
                    onClick={handleNext}
                    className="inline-flex items-center gap-2.5 bg-gradient-to-r from-primary to-cyan-400 hover:from-primary-700 hover:to-primary text-white px-8 sm:px-10 py-3.5 rounded-full font-semibold text-sm transition-all cursor-pointer shadow-lg shadow-primary/25"
                  >
                    {buttonText}
                    <ArrowRight size={16} />
                  </button>

                  {config.showProgressBar && (
                    <div className="flex items-center justify-center gap-2 mt-5">
                      <div className="flex gap-1">
                        {sortedModules.map((_, i) => (
                          <div
                            key={i}
                            className={`h-2 rounded-full transition-all ${
                              i < module.order
                                ? i === module.order - 1
                                  ? 'w-5 bg-primary'
                                  : 'w-2 bg-primary/50'
                                : 'w-2 bg-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-xs text-gray-400 font-medium tracking-wider">
                        PROGRESS {module.order}/{totalModules}
                      </span>
                    </div>
                  )}

                  {config.showFinalAssessmentLabel && nextModule && (
                    <p className="text-xs text-gray-400 font-medium tracking-wider uppercase mt-4">
                      MODULE {nextModule.order}: FINAL ASSESSMENT IS NEXT
                    </p>
                  )}
                </>
              )}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}